import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Button, TextField,
  Tab, Tabs, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Divider, Alert, CircularProgress,
  Card, CardContent, Tooltip, Stack, InputAdornment
} from '@mui/material';
import {
  Add, Delete, Print, Visibility, FilterList, Search,
  Receipt, AccountBalance, Assessment, Close,
  CheckCircle, HourglassEmpty, Warning, Payment as PaymentIcon
} from '@mui/icons-material';
import { billingAPI, patientAPI } from '../services/api';
import InvoiceForm from './InvoiceForm';

const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Credit (Hold)', 'Insurance', 'Cheque', 'Other'];
const MODE_COLORS = {
  Cash: '#2e7d32', Card: '#1565c0', UPI: '#6a1b9a', 'Credit (Hold)': '#9c27b0',
  Insurance: '#e65100', Cheque: '#00695c', Other: '#424242'
};
const STATUS_COLORS = { Paid: 'success', Pending: 'warning', Partial: 'info' };
const STATUS_ICONS = {
  Paid: <CheckCircle fontSize="small" />,
  Pending: <HourglassEmpty fontSize="small" />,
  Partial: <Warning fontSize="small" />
};

const fmt = (n) => `₹${Math.round(parseFloat(n) || 0).toLocaleString('en-IN')}`;

// Use local date (not UTC) so IST users see the correct date
const localDate = (d = new Date()) => {
  const dd = new Date(d);
  return `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}-${String(dd.getDate()).padStart(2, '0')}`;
};
const today = () => localDate();
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };

// ── Print helpers ─────────────────────────────────────────────────────────────

// Shared CSS for all A5 receipts
const RECEIPT_CSS = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  @page { size: A5; margin: 8mm; }
  body { font-family: 'Arial', sans-serif; font-size: 9pt; }
  .bill { border: 1px solid #000; padding: 5mm; page-break-after: always; break-after: page; }
  .bill.last-page { page-break-after: auto; break-after: auto; }
  .header { border-bottom: 1px dashed #000; padding-bottom: 3mm; margin-bottom: 3mm; }
  .header-top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 2mm; }
  .header-logo { width: 22mm; height: auto; object-fit: contain; }
  .header-center { flex: 1; text-align: center; padding: 0 2mm; }
  .header-center h1 { font-size: 12pt; margin-bottom: 1mm; font-weight: bold; line-height: 1.2; }
  .header-center h2 { font-size: 8pt; margin-bottom: 1mm; font-weight: 600; line-height: 1.2; }
  .header-center .address { font-size: 7pt; line-height: 1.3; margin: 0; }
  .bill-details { text-align: center; margin-top: 2mm; }
  .bill-details p { font-size: 8pt; margin: 0.5mm 0; line-height: 1.2; }
  .page-num { text-align: right; font-size: 7pt; color: #666; margin-bottom: 1mm; }
  .section { margin: 2mm 0; }
  .section-title { font-weight: bold; font-size: 9pt; border-bottom: 1px solid #000; margin-bottom: 2mm; padding-bottom: 1mm; }
  .row { display: flex; justify-content: space-between; margin: 1mm 0; font-size: 8pt; line-height: 1.3; }
  .info-row { justify-content: flex-start; gap: 2mm; }
  .info-row span:first-child { font-weight: bold; }
  .patient-info-container { display: flex; justify-content: space-between; align-items: flex-start; }
  .patient-info-right { text-align: left; max-width: 50%; font-size: 8pt; line-height: 1.3; }
  .items-table { width: 100%; margin: 2mm 0; border-collapse: collapse; }
  .items-table th { text-align: left; border-bottom: 1px solid #000; padding: 1.5mm 0; font-size: 8pt; font-weight: bold; }
  .items-table td { padding: 1mm 0; font-size: 8pt; line-height: 1.2; }
  .item-name  { width: 35%; }
  .item-qty   { width: 10%; text-align: center; }
  .item-price { width: 15%; text-align: right; }
  .item-gst   { width: 10%; text-align: right; font-size: 7pt; }
  .item-total { width: 15%; text-align: right; }
  .item-amt   { width: 15%; text-align: right; }
  .totals { border-top: 1px dashed #000; padding-top: 2mm; margin-top: 2mm; }
  .totals .row { font-size: 9pt; margin: 1mm 0; }
  .totals .final-total { font-size: 11pt; font-weight: bold; border-top: 1px solid #000; padding-top: 1.5mm; margin-top: 1.5mm; }
  .footer { text-align: center; border-top: 1px dashed #000; padding-top: 2mm; margin-top: 3mm; font-size: 7pt; line-height: 1.3; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .no-print { display: none; } }
  @media screen { body { max-width: 148mm; margin: 0 auto; background: #f0f0f0; padding: 10mm; } }`;

// Shared clinic header block
const clinicHeader = (titleLine, invoiceNum, dateStr) => `
  <div class="header">
    <div class="header-top">
      <img src="/logo.jpg" alt="Logo" class="header-logo" />
      <div class="header-center">
        <h1>GLOW with SuperDoc</h1>
        <h2>MSG Hotels &amp; Resorts</h2>
        <p class="address">Opp. MSG International School</p>
        <p class="address">Nezia Khera, Sirsa</p>
        <p class="address">Haryana, India</p>
        <p class="address">Pin Code 125055</p>
        <p class="address">GST NO: 06AAJCA4867D4ZL</p>
      </div>
      <img src="/logo2.png" alt="Logo" class="header-logo" />
    </div>
    <div class="bill-details">
      <p><strong>${titleLine}</strong></p>
      <p>Invoice No: ${invoiceNum}</p>
      <p>Date: ${dateStr}</p>
    </div>
  </div>`;

// Build paginated pages HTML from item rows
// Each page repeats the header; totals only on last page
function buildPages({ headerHTML, patientHTML, itemsHeaderHTML, itemRows, totalsHTML, servedByHTML, isSale, itemsPerPage = 12 }) {
  const chunks = [];
  for (let i = 0; i < itemRows.length; i += itemsPerPage) {
    chunks.push(itemRows.slice(i, i + itemsPerPage));
  }
  if (chunks.length === 0) chunks.push([]);
  const totalPages = chunks.length;

  return chunks.map((chunk, pageIdx) => {
    const isLast = pageIdx === totalPages - 1;
    return `
      <div class="bill${isLast ? ' last-page' : ''}">
        ${headerHTML}
        ${patientHTML}
        ${totalPages > 1 ? `<div class="page-num">Page ${pageIdx + 1} of ${totalPages}</div>` : ''}
        <div class="section">
          <div class="section-title">${isSale ? 'Items' : 'Services'}</div>
          <table class="items-table">
            <thead>${itemsHeaderHTML}</thead>
            <tbody>${chunk.join('')}</tbody>
          </table>
        </div>
        ${isLast ? totalsHTML + servedByHTML : ''}
        <div class="footer">
          ${isLast
        ? '<p>Thank you for visiting Glow With SuperDoc!</p><p>Please keep this receipt for your records</p>'
        : `<p style="font-style:italic">Continued on next page (${pageIdx + 1}/${totalPages})</p>`}
        </div>
      </div>`;
  }).join('');
}

// Unified A5 receipt for all invoice types (Sale, Treatment, Visitation, Other)
async function printReceipt(invoice) {
  const items = invoice.items || [];
  const payments = invoice.payments || [];
  const isSale = invoice.type === 'Sale';

  let patientAddress = '';
  if (invoice.patientId) {
    try {
      const res = await patientAPI.getById(invoice.patientId);
      const p = res.data.data;
      if (p) {
        patientAddress = [p.street, p.city, p.state, p.zipCode].filter(Boolean).join(', ');
      }
    } catch (err) {
      console.error('Failed to fetch patient address for printing:', err);
    }
  }

  const typeTitle = {
    Sale: 'Medicine Sales Receipt',
    Treatment: 'Treatment Invoice',
    Visitation: 'Consultation Invoice',
    Other: 'Invoice',
  }[invoice.type] || 'Invoice';

  // Payment status block
  let paymentFooter = '';
  if (invoice.paymentStatus === 'Paid') {
    const modes = [...new Set(payments.map(p => p.mode).filter(Boolean))].join(', ');
    paymentFooter = `
      <div class="row" style="color:#2e7d32;font-weight:bold"><span>Payment Status:</span><span>PAID ✓</span></div>
      ${modes ? `<div class="row"><span>Mode:</span><span>${modes}</span></div>` : ''}`;
  } else if (invoice.paymentStatus === 'Partial') {
    paymentFooter = `
      <div class="row" style="color:#1565c0"><span>Paid:</span><span>₹${Math.round(parseFloat(invoice.paidAmount || 0)).toLocaleString('en-IN')}</span></div>
      <div class="row" style="color:#e65100"><span>Balance Due:</span><span>₹${Math.round(parseFloat(invoice.dueAmount || 0)).toLocaleString('en-IN')}</span></div>`;
  } else {
    paymentFooter = `<div class="row" style="color:#e65100"><span>Payment Status:</span><span>Pending — Please pay at billing</span></div>`;
  }

  // Normalize item fields — legacy Sale records use medicineName/price/total,
  // Invoice records use description/unitPrice/amount
  const normItems = items.map(item => {
    const isLooseItem = item.isLoose;
    const baseDesc = item.description || item.medicineName || '—';
    return {
      desc: isLooseItem ? `${baseDesc} (loose)` : baseDesc,
      qtyLabel: isLooseItem
        ? `${item.quantity} tablet${item.quantity !== 1 ? 's' : ''}`
        : String(item.quantity || 1),
      unitPrice: Math.round(parseFloat(item.unitPrice || item.price || 0)),
      amount: Math.round(parseFloat(item.amount || item.total || 0)),
    };
  });

  const itemsHeaderHTML = `<tr>
    <th class="item-name">${isSale ? 'Medicine' : 'Description'}</th>
    <th class="item-qty">Qty</th>
    <th class="item-price">Rate</th>
    <th class="item-total">Amount</th>
    <th class="item-gst">GST<br/>(5%)</th>
    <th class="item-amt">Total</th>
  </tr>`;

  const itemRows = normItems.map(item => {
    const totalWithGST = item.amount;
    const amtBeforeGST = Math.round(totalWithGST / 1.05);
    const gstAmt = Math.round(totalWithGST - amtBeforeGST);
    const priceBeforeGST = Math.round(item.unitPrice / 1.05);
    return `<tr>
      <td class="item-name">${item.desc}</td>
      <td class="item-qty">${item.qtyLabel}</td>
      <td class="item-price">₹${priceBeforeGST.toLocaleString('en-IN')}</td>
      <td class="item-total">₹${amtBeforeGST.toLocaleString('en-IN')}</td>
      <td class="item-gst">₹${gstAmt.toLocaleString('en-IN')}</td>
      <td class="item-amt">₹${totalWithGST.toLocaleString('en-IN')}</td>
    </tr>`;
  });

  const taxableAmt = Math.round(parseFloat(invoice.totalAmount || 0) - parseFloat(invoice.discount || 0));
  const subtotalBeforeGST = Math.round(taxableAmt / 1.05);
  const totalGST = taxableAmt - subtotalBeforeGST;
  const halfGST = Math.round(totalGST / 2);

  const totalsHTML = `
    <div class="totals">
      <div class="row"><span>Subtotal (Before GST):</span><span>₹${subtotalBeforeGST.toLocaleString('en-IN')}</span></div>
      ${parseFloat(invoice.discount) > 0 ? `<div class="row"><span>Discount (${invoice.discountPercentage || 0}%):</span><span>- ₹${Math.round(parseFloat(invoice.discount)).toLocaleString('en-IN')}</span></div>` : ''}
      <div class="row"><span>CGST (2.5%):</span><span>₹${halfGST.toLocaleString('en-IN')}</span></div>
      <div class="row"><span>SGST (2.5%):</span><span>₹${(totalGST - halfGST).toLocaleString('en-IN')}</span></div>
      <div class="row"><span>Total GST (5%):</span><span>₹${totalGST.toLocaleString('en-IN')}</span></div>
      <div class="row final-total"><span>Total Amount:</span><span>₹${Math.round(parseFloat(invoice.totalAmount || 0)).toLocaleString('en-IN')}</span></div>
      ${paymentFooter}
    </div>`;

  const pagesHTML = buildPages({
    headerHTML: clinicHeader(typeTitle, invoice.invoiceNumber, `${invoice.invoiceDate} ${new Date(invoice.createdAt || new Date()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}`),
    patientHTML: `
      <div class="section">
        <div class="section-title">Patient Details</div>
        <div class="patient-info-container">
          <div>
            <div class="row info-row"><span>Name:</span><span>${invoice.patientName}</span></div>
            ${invoice.patientPhone ? `<div class="row info-row"><span>Phone:</span><span>${invoice.patientPhone}</span></div>` : ''}
          </div>
          ${patientAddress ? `<div class="patient-info-right"><strong>Address:</strong> ${patientAddress}</div>` : ''}
        </div>
      </div>`,
    itemsHeaderHTML,
    itemRows,
    totalsHTML,
    servedByHTML: `<div class="section"><div class="row"><span>Served By:</span><span>${invoice.createdBy || '—'}</span></div></div>`,
    isSale,
  });

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Receipt - ${invoice.invoiceNumber}</title>
  <style>${RECEIPT_CSS}</style>
</head>
<body>
  ${pagesHTML}
  <div class="no-print" style="text-align:center;margin-top:20px">
    <button onclick="window.print()" style="padding:10px 20px;font-size:16px;cursor:pointer;">Print</button>
    <button onclick="window.close()" style="padding:10px 20px;font-size:16px;cursor:pointer;margin-left:10px;">Close</button>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

function printReport(reportData, filters) {
  const { stats, invoices } = reportData;
  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Billing Report</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #222; padding: 30px; }
    .clinic-name { font-size: 20px; font-weight: 700; color: #1a237e; }
    .report-title { font-size: 15px; font-weight: 600; margin-top: 4px; }
    .period { color: #555; margin-top: 2px; }
    .divider { border: none; border-top: 2px solid #1a237e; margin: 12px 0; }
    .section-title { font-weight: 700; font-size: 11px; text-transform: uppercase;
                     color: #555; letter-spacing: 0.5px; margin: 14px 0 6px; }
    .stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
    .stat-card { padding: 10px 14px; background: #f5f5f5; border-radius: 6px; }
    .stat-label { font-size: 10px; color: #666; text-transform: uppercase; }
    .stat-value { font-size: 16px; font-weight: 700; color: #1a237e; margin-top: 2px; }
    .mode-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
    .mode-card { padding: 8px 12px; border-left: 4px solid #1a237e; background: #f5f5f5; border-radius: 0 4px 4px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 11px; }
    th { background: #1a237e; color: white; padding: 6px 8px; text-align: left; }
    td { padding: 5px 8px; border-bottom: 1px solid #eee; }
    td.right, th.right { text-align: right; }
    .status-Paid { color: #2e7d32; font-weight: 600; }
    .status-Pending { color: #e65100; font-weight: 600; }
    .status-Partial { color: #1565c0; font-weight: 600; }
    .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; }
  </style>
</head>
<body>
  <div style="display:flex;justify-content:space-between;align-items:flex-start">
    <div>
      <div class="clinic-name">Glow With SuperDoc</div>
      <div class="report-title">Billing Report</div>
      <div class="period">Period: ${filters.startDate} to ${filters.endDate}${filters.type ? ` | Type: ${filters.type}` : ''}</div>
    </div>
    <div style="text-align:right;color:#555;font-size:11px">
      Generated: ${new Date().toLocaleString('en-IN')}
    </div>
  </div>
  <hr class="divider" />

  <div class="stat-grid">
    <div class="stat-card"><div class="stat-label">Total Invoices</div><div class="stat-value">${stats.totalInvoices}</div></div>
    <div class="stat-card"><div class="stat-label">Total Amount</div><div class="stat-value">₹${parseFloat(stats.totalAmount).toFixed(2)}</div></div>
    <div class="stat-card"><div class="stat-label">Total Collected</div><div class="stat-value" style="color:#2e7d32">₹${parseFloat(stats.totalPaid).toFixed(2)}</div></div>
    <div class="stat-card"><div class="stat-label">Outstanding</div><div class="stat-value" style="color:#e65100">₹${parseFloat(stats.totalDue).toFixed(2)}</div></div>
    <div class="stat-card"><div class="stat-label">Excess Paid</div><div class="stat-value" style="color:#6a1b9a">₹${parseFloat(stats.totalExcess || 0).toFixed(2)}</div></div>
  </div>

  <div class="section-title">Payment Mode Breakdown</div>
  <div class="mode-grid">
    ${Object.entries(stats.byMode || {}).filter(([, v]) => v > 0).map(([mode, total]) =>
    `<div class="mode-card"><div style="font-size:10px;color:#666">${mode}</div><div style="font-weight:700">₹${parseFloat(total).toFixed(2)}</div></div>`
  ).join('')}
  </div>

  <div class="section-title">Invoice Breakdown by Type</div>
  <div class="mode-grid">
    ${Object.entries(stats.byType || {}).filter(([, v]) => v > 0).map(([type, total]) =>
    `<div class="mode-card"><div style="font-size:10px;color:#666">${type}</div><div style="font-weight:700">₹${parseFloat(total).toFixed(2)}</div></div>`
  ).join('')}
  </div>

  <div class="section-title">Invoice Listing (${invoices.length} invoices)</div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Invoice No.</th>
        <th>Date</th>
        <th>Patient</th>
        <th>Type</th>
        <th>Mode</th>
        <th class="right">Total</th>
        <th class="right">Paid</th>
        <th class="right">Due</th>
        <th class="right">Excess</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      ${invoices.map((inv, i) => {
    const modes = [...new Set((inv.payments || []).filter(p => parseFloat(p.amount) > 0).map(p => p.mode).filter(Boolean))];
    return `
        <tr>
          <td>${i + 1}</td>
          <td>${inv.invoiceNumber}</td>
          <td>${inv.invoiceDate}</td>
          <td>${inv.patientName}</td>
          <td>${inv.type}</td>
          <td>${modes.length > 0 ? modes.join(', ') : '—'}</td>
          <td class="right">₹${parseFloat(inv.totalAmount).toFixed(2)}</td>
          <td class="right">₹${parseFloat(inv.paidAmount).toFixed(2)}</td>
          <td class="right">₹${parseFloat(inv.dueAmount).toFixed(2)}</td>
          <td class="right" style="color:#6a1b9a">${parseFloat(inv.excessAmount || 0) > 0 ? '₹' + parseFloat(inv.excessAmount).toFixed(2) : '—'}</td>
          <td class="status-${inv.paymentStatus}">${inv.paymentStatus}</td>
        </tr>`;
  }).join('')}
      <tr style="font-weight:700;background:#f5f5f5">
        <td colspan="6">TOTAL</td>
        <td class="right">₹${parseFloat(stats.totalAmount).toFixed(2)}</td>
        <td class="right">₹${parseFloat(stats.totalPaid).toFixed(2)}</td>
        <td class="right">₹${parseFloat(stats.totalDue).toFixed(2)}</td>
        <td class="right" style="color:#6a1b9a">₹${parseFloat(stats.totalExcess || 0).toFixed(2)}</td>
        <td></td>
      </tr>
    </tbody>
  </table>

  <div class="footer">Glow With SuperDoc — Confidential Billing Report</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
  win.onload = () => win.print();
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ title, value, subtitle, color }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${color}` }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h6" fontWeight={700} sx={{ color, mt: 0.3 }}>{value}</Typography>
        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

function InvoiceDetailDialog({ invoice, onClose, onAddPayment }) {
  if (!invoice) return null;
  const payments = invoice.payments || [];
  const items = invoice.items || [];

  return (
    <Dialog open={!!invoice} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{invoice.invoiceNumber}</Typography>
          <Chip
            size="small"
            label={invoice.paymentStatus}
            color={STATUS_COLORS[invoice.paymentStatus]}
            icon={STATUS_ICONS[invoice.paymentStatus]}
            sx={{ mt: 0.5 }}
          />
        </Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}><Typography variant="caption" color="text.secondary">Patient</Typography>
            <Typography variant="body2" fontWeight={600}>{invoice.patientName}</Typography>
            {invoice.patientPhone && <Typography variant="caption" color="text.secondary">{invoice.patientPhone}</Typography>}
          </Grid>
          <Grid item xs={3}><Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography variant="body2">{invoice.invoiceDate}</Typography>
          </Grid>
          <Grid item xs={3}><Typography variant="caption" color="text.secondary">Type</Typography>
            <Typography variant="body2"><Chip label={invoice.type} size="small" /></Typography>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" color="primary" gutterBottom>Items</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>Description</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2">{it.description}</Typography>
                    <Typography variant="caption" color="text.secondary">{it.category}</Typography>
                  </TableCell>
                  <TableCell align="right">{it.quantity}</TableCell>
                  <TableCell align="right">{fmt(it.unitPrice)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 500 }}>{fmt(it.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ width: 260 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="body2">{fmt(invoice.subtotal)}</Typography>
            </Box>
            {parseFloat(invoice.discount) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Discount ({invoice.discountPercentage}%)</Typography>
                <Typography variant="body2" color="error.main">- {fmt(invoice.discount)}</Typography>
              </Box>
            )}
            {parseFloat(invoice.tax) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Tax ({invoice.taxPercentage}%)</Typography>
                <Typography variant="body2" color="success.main">+ {fmt(invoice.tax)}</Typography>
              </Box>
            )}
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
              <Typography variant="subtitle2" fontWeight={700} color="primary">{fmt(invoice.totalAmount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="success.main">Paid</Typography>
              <Typography variant="body2" color="success.main">{fmt(invoice.paidAmount)}</Typography>
            </Box>
            {parseFloat(invoice.dueAmount) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="error.main">Due</Typography>
                <Typography variant="body2" color="error.main">{fmt(invoice.dueAmount)}</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {payments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="primary" gutterBottom>Payment History</Typography>
            <Stack spacing={1}>
              {payments.map((p, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={p.mode} size="small"
                    sx={{ bgcolor: MODE_COLORS[p.mode] + '22', color: MODE_COLORS[p.mode], fontWeight: 600, minWidth: 70 }} />
                  <Typography variant="body2" fontWeight={600}>{fmt(p.amount)}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.paymentDate}</Typography>
                  {p.reference && <Typography variant="caption" color="text.secondary">({p.reference})</Typography>}
                </Box>
              ))}
            </Stack>
          </>
        )}

        {invoice.notes && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography variant="body2">{invoice.notes}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        {invoice.paymentStatus !== 'Paid' && (
          <Button variant="outlined" startIcon={<PaymentIcon />} onClick={() => onAddPayment(invoice)} size="small">
            Record Payment
          </Button>
        )}
        <Button variant="contained" startIcon={<Print />}
          onClick={() => printReceipt(invoice)} size="small">
          Print Invoice
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddPaymentDialog({ invoice, onClose, onSaved }) {
  const [rows, setRows] = useState([{ mode: 'Cash', amount: '', reference: '', paymentDate: today() }]);
  const [discountAmount, setDiscountAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (invoice) {
      const due = parseFloat(invoice.dueAmount) || 0;
      setRows([{ mode: 'Cash', amount: due > 0 ? due.toFixed(2) : '', reference: '', paymentDate: today() }]);
      setDiscountAmount('');
      setError('');
    }
  }, [invoice]);

  const updateRow = (idx, field, val) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: val };
      // Auto-fill full remaining due if amount is empty when selecting any mode (like Credit (Hold))
      if (field === 'mode' && (!updated.amount || parseFloat(updated.amount) === 0)) {
        updated.amount = effectiveDue > 0 ? effectiveDue.toFixed(2) : '';
      }
      return updated;
    }));
  };

  const addRow = () => setRows(prev => [...prev, { mode: 'Cash', amount: '', reference: '', paymentDate: today() }]);
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  const totalEntered = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const due = parseFloat(invoice?.dueAmount) || 0;
  const discAmt = parseFloat(discountAmount) || 0;
  const effectiveDue = Math.max(0, due - discAmt);

  const handleFillFullDue = (idx = 0) => {
    if (rows.length > idx) {
      updateRow(idx, 'amount', effectiveDue.toFixed(2));
    }
  };

  const handleSave = async () => {
    let toSubmitRows = rows;
    let valid = toSubmitRows.filter(r => parseFloat(r.amount) > 0);

    // If user left amount blank but selected a mode (e.g. Credit (Hold)), auto-use effective due
    if (!valid.length && discAmt <= 0 && effectiveDue > 0) {
      valid = [{
        mode: rows[0]?.mode || 'Credit (Hold)',
        amount: effectiveDue.toFixed(2),
        reference: rows[0]?.reference || (rows[0]?.mode === 'Credit (Hold)' ? 'AUTH-HOLD' : ''),
        paymentDate: rows[0]?.paymentDate || today()
      }];
    }

    if (!valid.length && discAmt <= 0) { setError('Enter at least one payment amount or a discount'); return; }
    if (discAmt > due + 0.01) { setError(`Discount (${fmt(discAmt)}) exceeds due amount (${fmt(due)})`); return; }
    setSaving(true);
    setError('');
    try {
      await billingAPI.addPayment(invoice.id, { payments: valid, discountAmount: discAmt, source: invoice.source });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  if (!invoice) return null;

  const hasCreditHoldRow = rows.some(r => r.mode === 'Credit (Hold)');

  return (
    <Dialog open={!!invoice} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Record Payment</Typography>
          <Typography variant="caption" color="text.secondary">
            {invoice.invoiceNumber} — Due: {fmt(invoice.dueAmount)}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {hasCreditHoldRow && (
          <Alert severity="info" sx={{ mb: 2, bgcolor: '#f3e5f5', color: '#6a1b9a', borderColor: '#ce93d8' }}>
            🏛️ <strong>Credit (Hold) Selected:</strong> This patient's balance of <strong>{fmt(effectiveDue)}</strong> will be placed on hold for authority settlement.
          </Alert>
        )}
        <TextField
          fullWidth size="small" label="Discount Amount ₹" type="number"
          value={discountAmount}
          inputProps={{ min: 0, step: '0.01', max: due }}
          onChange={e => setDiscountAmount(e.target.value)}
          sx={{ mb: 2 }}
          helperText={discAmt > 0 ? `Effective due after discount: ${fmt(effectiveDue)}` : 'Optional: apply a discount before recording payment'}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Split payment across multiple modes if needed.
          </Typography>
          {totalEntered < effectiveDue && (
            <Button size="small" variant="text" onClick={() => handleFillFullDue(0)}>
              Auto-fill Due ({fmt(effectiveDue)})
            </Button>
          )}
        </Stack>
        {rows.map((row, idx) => (
          <Grid container spacing={2} key={idx} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select value={row.mode} label="Mode"
                  onChange={e => updateRow(idx, 'mode', e.target.value)}>
                  {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Amount ₹" type="number"
                value={row.amount} inputProps={{ min: 0, step: '0.01' }}
                placeholder={effectiveDue.toFixed(2)}
                onChange={e => updateRow(idx, 'amount', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="Reference"
                value={row.reference}
                placeholder={row.mode === 'Credit (Hold)' ? 'Authority Ref' : 'Ref / Txn ID'}
                onChange={e => updateRow(idx, 'reference', e.target.value)} />
            </Grid>
            <Grid item xs={2}>
              <TextField fullWidth size="small" label="Date" type="date"
                value={row.paymentDate} InputLabelProps={{ shrink: true }}
                onChange={e => updateRow(idx, 'paymentDate', e.target.value)} />
            </Grid>
            <Grid item xs={1}>
              <IconButton size="small" onClick={() => removeRow(idx)} disabled={rows.length === 1}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button size="small" startIcon={<Add />} onClick={addRow}>Add Mode</Button>
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {discAmt > 0 && <Chip label={`Discount: ${fmt(discAmt)}`} color="warning" size="small" />}
          <Chip label={`Entering: ${fmt(totalEntered)}`} color="primary" size="small" />
          {totalEntered > effectiveDue
            ? <Chip label={`Excess: ${fmt(totalEntered - effectiveDue)}`} color="error" size="small" />
            : <Chip label={`Remaining: ${fmt(effectiveDue - totalEntered)}`}
              color={totalEntered >= effectiveDue ? 'success' : 'warning'} size="small" />
          }
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Billing Page ─────────────────────────────────────────────────────────

export default function Billing() {
  const [tab, setTab] = useState(0);

  // Filters
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(today());
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [search, setSearch] = useState('');

  // Data
  const [invoices, setInvoices] = useState([]);
  const [paymentsData, setPaymentsData] = useState({ payments: [], summary: {}, grandTotal: 0 });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dialogs
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [prePopulate, setPrePopulate] = useState(null); // pre-fill from session/visitation
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonError, setDeleteReasonError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (invoice) => {
    if (!invoice) return;
    if (!deleteReason.trim()) {
      setDeleteReasonError('Please provide a reason for deleting this invoice');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await billingAPI.deleteInvoice(invoice.id, {
        reason: deleteReason.trim(),
        source: invoice.source
      });
      setDeleteConfirm(null);
      setDeleteReason('');
      setDeleteReasonError('');
      fetchInvoices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  // ── Data fetching ───────────────────────────────────────────────────────────

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await billingAPI.getInvoices({
        startDate, endDate,
        type: typeFilter || undefined,
        paymentStatus: statusFilter || undefined,
        search: search || undefined
      });
      setInvoices(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, typeFilter, statusFilter, search]);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await billingAPI.getPayments({
        startDate, endDate,
        mode: modeFilter || undefined,
        invoiceType: typeFilter || undefined
      });
      setPaymentsData(res.data || { payments: [], summary: {}, grandTotal: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, modeFilter, typeFilter]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await billingAPI.getReport({
        startDate, endDate,
        type: typeFilter || undefined
      });
      setReportData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, typeFilter]);

  useEffect(() => {
    if (tab === 0) fetchInvoices();
    else if (tab === 1) fetchPayments();
    else fetchReport();
  }, [tab, fetchInvoices, fetchPayments, fetchReport]);

  // ── Aggregate stats for summary cards (from invoices) ──────────────────────
  const totalAmount = invoices.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + (parseFloat(i.paidAmount) || 0), 0);
  const totalDue = invoices.reduce((s, i) => s + (parseFloat(i.dueAmount) || 0), 0);

  // ── Filter bar (shared across tabs) ────────────────────────────────────────
  const FilterBar = (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" label="From Date" type="date"
            value={startDate} InputLabelProps={{ shrink: true }}
            onChange={e => setStartDate(e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" label="To Date" type="date"
            value={endDate} InputLabelProps={{ shrink: true }}
            onChange={e => setEndDate(e.target.value)} />
        </Grid>
        <Grid item xs={6} sm={2.2}>
          <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={e => setTypeFilter(e.target.value)}>
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="Visitation">Visitation</MenuItem>
              <MenuItem value="Treatment">Treatment</MenuItem>
              <MenuItem value="Sale">Sale</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {tab === 0 && (
          <Grid item xs={6} sm={2.2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Partial">Partial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        )}
        {tab === 1 && (
          <Grid item xs={6} sm={2.2}>
            <FormControl fullWidth size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Mode</InputLabel>
              <Select value={modeFilter} label="Mode" onChange={e => setModeFilter(e.target.value)}>
                <MenuItem value="">All Modes</MenuItem>
                {PAYMENT_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        )}
        {tab === 0 && (
          <Grid item xs={12} sm={2.8}>
            <TextField fullWidth size="small" label="Search" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              placeholder="Invoice #, patient name/phone" />
          </Grid>
        )}
        <Grid item xs={12} sm="auto">
          <Button variant="contained" startIcon={<FilterList />} size="small"
            onClick={() => { if (tab === 0) fetchInvoices(); else if (tab === 1) fetchPayments(); else fetchReport(); }}>
            Apply
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  // ── Invoices Tab ────────────────────────────────────────────────────────────
  const InvoicesTab = (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <SummaryCard title="Invoices" value={invoices.length} color="#1565c0" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard title="Total Billed" value={fmt(totalAmount)} color="#4a148c" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard title="Collected" value={fmt(totalPaid)} color="#1b5e20" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <SummaryCard title="Outstanding" value={fmt(totalDue)} color="#e65100" />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button variant="contained" startIcon={<Add />}
          onClick={() => { setEditInvoice(null); setShowCreateForm(true); }}>
          New Invoice
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Invoice #', 'Date', 'Patient', 'Type', 'Mode', 'Total', 'Paid', 'Due', 'Status', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ bgcolor: 'primary.main', backgroundImage: 'none', color: 'primary.contrastText', fontWeight: 600, py: 1.2 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No invoices found for selected filters
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv, rowIdx) => {
                  // Legacy sale records (pre-auto-invoice) are read-only
                  const isLegacySale = inv.source === 'sale';
                  const rowKey = `${inv.source || 'invoice'}-${inv.id}-${rowIdx}`;

                  return (
                    <TableRow key={rowKey} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main' }}>
                          {inv.invoiceNumber}
                        </Typography>
                        {isLegacySale && (
                          <Typography variant="caption" color="text.secondary">Medicine sale</Typography>
                        )}
                      </TableCell>
                      <TableCell>{inv.invoiceDate}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{inv.patientName}</Typography>
                        {inv.patientPhone && (
                          <Typography variant="caption" color="text.secondary">{inv.patientPhone}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={inv.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const modes = [...new Set((inv.payments || []).filter(p => parseFloat(p.amount) > 0).map(p => p.mode).filter(Boolean))];
                          return modes.length > 0
                            ? modes.map(m => (
                              <Chip key={m} label={m} size="small"
                                sx={{ mr: 0.4, bgcolor: MODE_COLORS[m] + '22', color: MODE_COLORS[m], fontWeight: 600, fontSize: 10 }} />
                            ))
                            : <Typography variant="caption" color="text.secondary">—</Typography>;
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{fmt(inv.totalAmount)}</TableCell>
                      <TableCell sx={{ color: 'success.main' }}>{fmt(inv.paidAmount)}</TableCell>
                      <TableCell sx={{ color: parseFloat(inv.dueAmount) > 0 ? 'error.main' : 'text.secondary' }}>
                        {fmt(inv.dueAmount)}
                      </TableCell>
                      <TableCell>
                        <Chip label={inv.paymentStatus} color={STATUS_COLORS[inv.paymentStatus]} size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => setDetailInvoice(inv)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print Invoice">
                            <IconButton size="small" onClick={() => printReceipt(inv)}>
                              <Print fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {inv.paymentStatus !== 'Paid' && (
                            <Tooltip title="Record Payment">
                              <IconButton size="small" color="success"
                                onClick={() => setPaymentInvoice(inv)}>
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {inv.paymentStatus === 'Pending' && (
                            <Tooltip title="Delete Invoice">
                              <IconButton size="small" color="error"
                                onClick={() => {
                                  setDeleteConfirm(inv);
                                  setDeleteReason('');
                                  setDeleteReasonError('');
                                }}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ── Payments Tab ────────────────────────────────────────────────────────────
  const { payments, summary, grandTotal } = paymentsData;

  const PaymentsTab = (
    <Box>
      {/* Mode Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {PAYMENT_MODES.map(mode => {
          const s = summary[mode] || { count: 0, total: 0 };
          return (
            <Grid item xs={6} sm={4} md={2} key={mode}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderTop: `3px solid ${MODE_COLORS[mode]}` }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" fontWeight={600} sx={{ color: MODE_COLORS[mode], textTransform: 'uppercase' }}>
                    {mode}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ mt: 0.3 }}>
                    {fmt(s.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.count} transaction{s.count !== 1 ? 's' : ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700} color="primary">Grand Total Collected</Typography>
            <Typography variant="h5" fontWeight={700} color="primary">{fmt(grandTotal)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Payment transactions table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Date', 'Invoice #', 'Patient', 'Invoice Type', 'Mode', 'Reference', 'Amount'].map(h => (
                  <TableCell key={h} sx={{ bgcolor: 'primary.main', backgroundImage: 'none', color: 'primary.contrastText', fontWeight: 600, py: 1.2 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No payments found for selected filters
                  </TableCell>
                </TableRow>
              ) : (
                payments.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>{p.paymentDate}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main' }}>
                        {p.invoice?.invoiceNumber || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{p.invoice?.patientName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {p.invoice?.type && <Chip label={p.invoice.type} size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell>
                      <Chip label={p.mode} size="small"
                        sx={{ bgcolor: MODE_COLORS[p.mode] + '22', color: MODE_COLORS[p.mode], fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{p.reference || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: MODE_COLORS[p.mode] }}>{fmt(p.amount)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ── Reports Tab ─────────────────────────────────────────────────────────────
  const ReportsTab = (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !reportData ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Assessment sx={{ fontSize: 60, mb: 1, opacity: 0.3 }} />
          <Typography>Apply filters and click Apply to generate report</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Print />}
              onClick={() => printReport(reportData, { startDate, endDate, type: typeFilter })}>
              Print Full Report
            </Button>
          </Box>

          {/* Summary stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={2}>
              <SummaryCard title="Total Invoices" value={reportData.stats.totalInvoices} color="#1565c0" />
            </Grid>
            <Grid item xs={6} sm={2}>
              <SummaryCard title="Total Billed" value={fmt(reportData.stats.totalAmount)} color="#4a148c" />
            </Grid>
            <Grid item xs={6} sm={2}>
              <SummaryCard title="Total Collected" value={fmt(reportData.stats.totalPaid)} color="#1b5e20" />
            </Grid>
            <Grid item xs={6} sm={2}>
              <SummaryCard title="Outstanding" value={fmt(reportData.stats.totalDue)} color="#e65100" />
            </Grid>
            <Grid item xs={6} sm={2}>
              <SummaryCard title="Excess Paid" value={fmt(reportData.stats.totalExcess || 0)} color="#6a1b9a"
                subtitle={reportData.stats.totalExcess > 0 ? 'Overpayments' : undefined} />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* By Status */}
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>By Status</Typography>
                {Object.entries(reportData.stats.byStatus).map(([status, count]) => (
                  <Box key={status} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Chip label={status} size="small" color={STATUS_COLORS[status]} />
                    <Typography variant="body2" fontWeight={600}>{count} invoices</Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* By Type */}
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Revenue by Type</Typography>
                {Object.entries(reportData.stats.byType).map(([type, amount]) => (
                  <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{type}</Typography>
                    <Typography variant="body2" fontWeight={600}>{fmt(amount)}</Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* By Mode */}
            <Grid item xs={12} sm={4}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Collections by Mode</Typography>
                {Object.entries(reportData.stats.byMode).filter(([, v]) => v > 0).map(([mode, total]) => (
                  <Box key={mode} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Chip label={mode} size="small"
                      sx={{ bgcolor: MODE_COLORS[mode] + '22', color: MODE_COLORS[mode], fontWeight: 600 }} />
                    <Typography variant="body2" fontWeight={600}>{fmt(total)}</Typography>
                  </Box>
                ))}
                {Object.values(reportData.stats.byMode).every(v => !v) && (
                  <Typography variant="body2" color="text.secondary">No payments in this period</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Invoice list preview */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Invoice Listing ({reportData.invoices.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Invoice #', 'Date', 'Patient', 'Type', 'Mode', 'Total', 'Paid', 'Due', 'Excess', 'Status'].map(h => (
                      <TableCell key={h} sx={{ bgcolor: 'primary.main', backgroundImage: 'none', color: 'primary.contrastText', fontWeight: 600, py: 1 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.invoices.map(inv => {
                    const modes = [...new Set((inv.payments || []).filter(p => parseFloat(p.amount) > 0).map(p => p.mode).filter(Boolean))];
                    return (
                      <TableRow key={`${inv.source}-${inv.id}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'primary.main' }}>
                            {inv.invoiceNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>{inv.invoiceDate}</TableCell>
                        <TableCell>{inv.patientName}</TableCell>
                        <TableCell><Chip label={inv.type} size="small" variant="outlined" /></TableCell>
                        <TableCell>
                          {modes.length > 0
                            ? modes.map(m => (
                              <Chip key={m} label={m} size="small"
                                sx={{ mr: 0.4, bgcolor: MODE_COLORS[m] + '22', color: MODE_COLORS[m], fontWeight: 600, fontSize: 10 }} />
                            ))
                            : <Typography variant="caption" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell>{fmt(inv.totalAmount)}</TableCell>
                        <TableCell sx={{ color: 'success.main' }}>{fmt(inv.paidAmount)}</TableCell>
                        <TableCell sx={{ color: parseFloat(inv.dueAmount) > 0 ? 'error.main' : 'inherit' }}>
                          {fmt(inv.dueAmount)}
                        </TableCell>
                        <TableCell sx={{ color: parseFloat(inv.excessAmount) > 0 ? 'secondary.main' : 'inherit' }}>
                          {parseFloat(inv.excessAmount) > 0 ? fmt(inv.excessAmount) : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={inv.paymentStatus} size="small" color={STATUS_COLORS[inv.paymentStatus]} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{fmt(reportData.stats.totalAmount)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'success.main' }}>{fmt(reportData.stats.totalPaid)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'error.main' }}>{fmt(reportData.stats.totalDue)}</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'secondary.main' }}>{fmt(reportData.stats.totalExcess || 0)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Receipt sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight={700}>Billing</Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {FilterBar}

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<Receipt fontSize="small" />} iconPosition="start" label="Invoices" />
          <Tab icon={<AccountBalance fontSize="small" />} iconPosition="start" label="Payments" />
          <Tab icon={<Assessment fontSize="small" />} iconPosition="start" label="Reports" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {tab === 0 && InvoicesTab}
          {tab === 1 && PaymentsTab}
          {tab === 2 && ReportsTab}
        </Box>
      </Paper>

      {/* Create / Edit Invoice Form */}
      <InvoiceForm
        open={showCreateForm || !!editInvoice}
        onClose={() => { setShowCreateForm(false); setEditInvoice(null); setPrePopulate(null); }}
        onSaved={() => { fetchInvoices(); if (tab !== 0) setTab(0); }}
        editInvoice={editInvoice}
        prePopulate={prePopulate}
      />

      {/* Invoice Detail Dialog */}
      <InvoiceDetailDialog
        invoice={detailInvoice}
        onClose={() => setDetailInvoice(null)}
        onAddPayment={(inv) => { setDetailInvoice(null); setPaymentInvoice(inv); }}
      />

      {/* Add Payment Dialog */}
      <AddPaymentDialog
        invoice={paymentInvoice}
        onClose={() => setPaymentInvoice(null)}
        onSaved={() => { fetchInvoices(); if (tab === 1) fetchPayments(); }}
      />

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => !deleting && setDeleteConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" /> Delete Pending Invoice
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to delete pending invoice <strong>{deleteConfirm?.invoiceNumber}</strong> for <strong>{deleteConfirm?.patientName}</strong>?
          </Typography>
          <TextField
            fullWidth
            required
            autoFocus
            multiline
            rows={3}
            label="Reason for deletion *"
            placeholder="e.g. Duplicate entry, patient cancelled appointment, bill created by mistake..."
            value={deleteReason}
            onChange={(e) => {
              setDeleteReason(e.target.value);
              if (deleteReasonError) setDeleteReasonError('');
            }}
            error={!!deleteReasonError}
            helperText={deleteReasonError || "A reason is required to track deletion history."}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDelete(deleteConfirm)}
            disabled={deleting || !deleteReason.trim()}
          >
            {deleting ? 'Deleting…' : 'Delete Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
